import React, { useState, useRef, useEffect } from 'react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../services/firebase';
import { signOut } from 'firebase/auth';
import { User } from 'firebase/auth';
import { ProfileStats } from '../types';
import { logOnboardingCompleted } from '../services/analytics';
import { Shield, MapPin, Briefcase, Clock, User as UserIcon, AlertCircle, LogOut } from 'lucide-react';

interface OnboardingProps {
  user: User;
  onComplete: () => void;
}

const BODIES = [
  "Guardia Civil", "Policía Nacional", "Policía Local", "Mossos d'Esquadra",
  "Ertzaintza", "Policía Foral de Navarra", "Cuerpo General de la Policía Canaria"
];

const RANGES_YEARS = ["1-5", "6-10", "11-15", "16-20", "21+"];
const RANGES_AGE = ["<20", "21-29", "30-39", "40-49", "50+"];

const CCAA = [
  "Andalucía", "Aragón", "Asturias", "Baleares", "Canarias", "Cantabria",
  "Castilla-La Mancha", "Castilla y León", "Cataluña", "Comunidad Valenciana",
  "Extremadura", "Galicia", "La Rioja", "Madrid", "Murcia", "Navarra",
  "País Vasco", "Ceuta", "Melilla"
];

const Onboarding: React.FC<OnboardingProps> = ({ user, onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const [formData, setFormData] = useState<Partial<ProfileStats>>({
    cuerpo: undefined,
    ambito: '',
    añosServicio: undefined,
    edad: undefined,
    comunidadAutonoma: undefined
  });

  useEffect(() => {
    // Unblock global scroll during onboarding
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalBodyOverflow = document.body.style.overflow;

    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';

    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.body.style.overflow = originalBodyOverflow;
    };
  }, []);

  const handleChange = (field: keyof ProfileStats, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error al cerrar sesión", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setShowErrors(true);

    const isInvalid = !formData.cuerpo || !formData.ambito?.trim() || !formData.añosServicio || !formData.edad || !formData.comunidadAutonoma;

    if (isInvalid) {
      setError("Por favor, complete todos los campos para continuar.");

      // Auto-scroll al primer error
      setTimeout(() => {
        const firstError = formRef.current?.querySelector('.border-danger');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (firstError instanceof HTMLElement) firstError.focus();
        }
      }, 100);
      return;
    }

    setLoading(true);

    try {
      const userRef = doc(db, 'users', user.uid);

      const payload: Record<string, unknown> = {
        profileStats: formData,
        onboardingCompleted: true,
        updatedAt: serverTimestamp()
      };

      await setDoc(userRef, payload, { merge: true });

      logOnboardingCompleted(formData);

      onComplete();
    } catch (err) {
      console.error("Error guardando onboarding:", err);
      setError("Hubo un error al guardar los datos. Inténtelo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const getFieldClasses = (isInvalid: boolean) => {
    const base = "w-full min-h-11 bg-canvas border rounded-xl px-3 py-2.5 md:py-2 text-sm text-ink outline-none transition-all placeholder:text-muted";
    const status = isInvalid
      ? "border-danger focus:ring-1 focus:ring-danger ring-danger"
      : "border-line focus:border-juris focus:ring-1 focus:ring-juris";
    return `${base} ${status}`;
  };

  const renderErrorText = (isInvalid: boolean, errorId: string) => {
    if (!isInvalid) return null;
    return <p id={errorId} role="alert" className="text-danger text-xs mt-1 font-medium motion-fade-through-fast">Obligatorio</p>;
  };

  return (
    <div className="min-h-[100dvh] bg-canvas flex items-center justify-center p-4 md:p-6 font-sans overflow-y-auto overflow-x-hidden">

      <div className="bg-panel max-w-lg md:max-w-xl lg:max-w-2xl w-full rounded-2xl shadow-xl border border-line my-auto overflow-hidden">

        {/* Header */}
        <div className="bg-panel border-b border-line p-6 md:px-7 md:py-5 lg:py-4 text-ink text-center">
          <img src="/juris-logo.png" alt="Juris" width={72} height={72} className="h-[72px] w-[72px] mx-auto mb-3 object-contain" />
          <h1 className="text-2xl md:text-xl font-bold mb-1">Configuración Inicial</h1>
          <p className="text-juris text-sm leading-relaxed max-w-xl mx-auto">Para mejorar Juris y priorizar funciones para su labor, necesitamos conocer su perfil profesional.</p>
        </div>

        <form ref={formRef} onSubmit={handleSubmit} className="p-6 md:p-6 lg:p-7 space-y-5 md:space-y-4 lg:space-y-4">

          <div className="lg:grid lg:grid-cols-2 lg:gap-4">
            <div>
              <label htmlFor="cuerpo" className="block text-sm font-bold text-ink mb-1.5 flex items-center gap-2">
                <Shield size={16} className="text-juris" /> Cuerpo Policial
              </label>
              <select
                id="cuerpo"
                name="cuerpo"
                className={getFieldClasses(showErrors && !formData.cuerpo)}
                aria-invalid={showErrors && !formData.cuerpo}
                aria-describedby={showErrors && !formData.cuerpo ? 'cuerpo-error' : undefined}
                value={formData.cuerpo || ''}
                onChange={(e) => handleChange('cuerpo', e.target.value)}
              >
                <option value="" disabled>Seleccione su cuerpo</option>
                {BODIES.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
              {renderErrorText(showErrors && !formData.cuerpo, 'cuerpo-error')}
            </div>

            <div className="mt-5 lg:mt-0">
              <label htmlFor="ambito" className="block text-sm font-bold text-ink mb-1.5 flex items-center gap-2">
                <Briefcase size={16} className="text-juris" /> Ámbito Principal
              </label>
              <input
                id="ambito"
                name="ambito"
                type="text"
                placeholder="Seguridad Ciudadana, Tráfico, Extranjería..."
                className={getFieldClasses(showErrors && !formData.ambito?.trim())}
                aria-invalid={showErrors && !formData.ambito?.trim()}
                aria-describedby={showErrors && !formData.ambito?.trim() ? 'ambito-error' : undefined}
                value={formData.ambito}
                onChange={(e) => handleChange('ambito', e.target.value)}
              />
              {renderErrorText(showErrors && !formData.ambito?.trim(), 'ambito-error')}
            </div>
          </div>

          {/* 3. CCAA */}
          <div>
            <div>
            <label htmlFor="comunidadAutonoma" className="block text-sm font-bold text-ink mb-1.5 flex items-center gap-2">
              <MapPin size={16} className="text-juris" /> Comunidad Autónoma
            </label>
            <select
              id="comunidadAutonoma"
              name="comunidadAutonoma"
              className={getFieldClasses(showErrors && !formData.comunidadAutonoma)}
              aria-invalid={showErrors && !formData.comunidadAutonoma}
              aria-describedby={showErrors && !formData.comunidadAutonoma ? 'comunidad-error' : undefined}
              value={formData.comunidadAutonoma || ''}
              onChange={(e) => handleChange('comunidadAutonoma', e.target.value)}
            >
              <option value="" disabled>Seleccione zona de servicio</option>
              {CCAA.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {renderErrorText(showErrors && !formData.comunidadAutonoma, 'comunidad-error')}
            </div>
          </div>

          {/* 4. Grid para Años y Edad */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="añosServicio" className="block text-sm font-bold text-ink mb-1.5 flex items-center gap-2">
                <Clock size={16} className="text-juris" /> Antigüedad
              </label>
              <select
                id="añosServicio"
                name="añosServicio"
                className={getFieldClasses(showErrors && !formData.añosServicio)}
                aria-invalid={showErrors && !formData.añosServicio}
                aria-describedby={showErrors && !formData.añosServicio ? 'anos-servicio-error' : undefined}
                value={formData.añosServicio || ''}
                onChange={(e) => handleChange('añosServicio', e.target.value)}
              >
                <option value="" disabled>Años</option>
                {RANGES_YEARS.map(r => <option key={r} value={r}>{r} años</option>)}
              </select>
              {renderErrorText(showErrors && !formData.añosServicio, 'anos-servicio-error')}
            </div>
            <div>
              <label htmlFor="edad" className="block text-sm font-bold text-ink mb-1.5 flex items-center gap-2">
                <UserIcon size={16} className="text-juris" /> Edad
              </label>
              <select
                id="edad"
                name="edad"
                className={getFieldClasses(showErrors && !formData.edad)}
                aria-invalid={showErrors && !formData.edad}
                aria-describedby={showErrors && !formData.edad ? 'edad-error' : undefined}
                value={formData.edad || ''}
                onChange={(e) => handleChange('edad', e.target.value)}
              >
                <option value="" disabled>Edad</option>
                {RANGES_AGE.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              {renderErrorText(showErrors && !formData.edad, 'edad-error')}
            </div>
          </div>

          {error && (
            <div role="alert" className="bg-danger-soft text-danger text-sm p-3 rounded-xl flex items-center gap-2 border border-danger">
              <AlertCircle size={18} className="shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* BOTONERA DE ACCIÓN INTEGRADA */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={handleLogout}
              className="flex-1 min-h-11 py-3 bg-raised text-muted font-bold rounded-xl hover:bg-raised transition-colors flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-line"
            >
              <LogOut size={18} />
              <span className="text-sm">Salir</span>
            </button>

            <button
              type="submit"
              disabled={loading}
              className="flex-[2] min-h-11 py-3 bg-action text-white font-bold rounded-xl shadow-lg shadow-black hover:bg-action-hover transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-juris focus:ring-offset-2"
            >
              {loading ? 'Guardando...' : 'Continuar a Juris'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Onboarding;
